import React, { useState, useRef } from "react";
import { CustomizationCarousel } from "./common/CustomizationCarousel";
import { LogoPreview } from "./LogoPreview";
import { getLogoSource } from "../utils/logoUtils";
import { ColorPickerWithPresets } from './common/ColorPickerWithPresets';
import { AdvancedSettings } from './common/AdvancedSettings';
import { Slider } from './common/Slider';
import { Toggle } from './common/Toggle';
import styled from 'styled-components';

export type LogoType = 'modern-split' | 'circular' | 'minimal-frame' | 'tech-style' | 'instagram' | 'facebook' | 'custom';

interface LogoCustomizationProps {
  logo: {
    type: LogoType;
    src: string | null;
    width?: number;
    height?: number;
  } | null;
  setLogo: React.Dispatch<
    React.SetStateAction<{
      type: LogoType;
      src: string | null;
      width?: number;
      height?: number;
    } | null>
  >;
  customLogo: string;
  setCustomLogo: (logo: string | null) => void;
  logoSize: number;
  setLogoSize: React.Dispatch<React.SetStateAction<number>>;
  logoColor: string;
  setLogoColor: React.Dispatch<React.SetStateAction<string>>;
  gradient: boolean;
  setGradient: React.Dispatch<React.SetStateAction<boolean>>;
  gradientColor1: string;
  setGradientColor1: React.Dispatch<React.SetStateAction<string>>;
  gradientColor2: string;
  setGradientColor2: React.Dispatch<React.SetStateAction<string>>;
  gradientType: string;
  setGradientType: React.Dispatch<React.SetStateAction<string>>;
  gradientRotation: number;
  setGradientRotation: React.Dispatch<React.SetStateAction<number>>;
  cornerDots: string;
  setCornerDots: React.Dispatch<React.SetStateAction<string>>;
  cornerSquares: string;
  setCornerSquares: React.Dispatch<React.SetStateAction<string>>;
  hideBackground: boolean;
  setHideBackground: React.Dispatch<React.SetStateAction<boolean>>;
}

export const LogoCustomization: React.FC<LogoCustomizationProps> = ({
  logo,
  setLogo,
  customLogo,
  setCustomLogo,
  logoSize,
  setLogoSize,
  logoColor,
  setLogoColor,
  hideBackground,
  setHideBackground,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const logoOptions: { type: LogoType; label: string }[] = [
    { type: "custom", label: "Custom Upload" },
    { type: "modern-split", label: "Modern Split" },
    { type: "circular", label: "Circular" },
    { type: "minimal-frame", label: "Minimal Frame" },
    { type: "tech-style", label: "Tech Style" },
    { type: "instagram", label: "Instagram" },
    { type: "facebook", label: "Facebook" },
  ];

  const itemsPerPage = 6;
  const totalPages = Math.ceil(logoOptions.length / itemsPerPage);

  const handleLogoSelect = (type: LogoType) => {
    if (type === "custom") {
      fileInputRef.current?.click();
    } else {
      setLogo({
        type,
        src: getLogoSource(type),
        width: 50,
        height: 30,
      });
    }
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const maxSize = 50;
          const ratio = img.width / img.height;
          let width = maxSize;
          let height = maxSize;
          
          if (ratio > 1) {
            height = width / ratio;
          } else {
            width = height * ratio;
          }

          setLogo({
            type: "custom",
            src: e.target?.result as string,
            width,
            height,
          });
          setCustomLogo(e.target?.result as string);
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Section>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleLogoUpload}
        accept="image/*"
      />
      
      <CustomizationCarousel
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
      >
        {logoOptions
          .slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage)
          .map((option) => (
            <OptionBox
              key={option.type}
              active={logo?.type === option.type}
              onClick={() => handleLogoSelect(option.type)}
            >
              <PreviewContainer>
                <LogoPreview 
                  type={option.type} 
                  src={option.type === 'custom' ? customLogo : getLogoSource(option.type)}
                />
              </PreviewContainer>
              <OptionLabel>{option.label}</OptionLabel>
            </OptionBox>
        ))}
      </CustomizationCarousel>

      <AdvancedSettings
  showAdvanced={showAdvanced}
  setShowAdvanced={setShowAdvanced}
  titlePrefix="Advanced Logo"
>
        <ColorPickerWithPresets
          label="Logo Color"
          color={logoColor}
          onChange={setLogoColor}
        />
        <Slider
          label="Logo Size"
          min={10}
          max={100}
          value={logoSize}
          onChange={setLogoSize}
        />
        <Toggle
          label="Hide Background"
          checked={hideBackground}
          onChange={setHideBackground}
        />
      </AdvancedSettings>
    </Section>
  );
};

const Section = styled.section`
  margin-top: 24px;
`;

const OptionBox = styled.button<{ active: boolean }>`
  min-width: 120px;
  height: 120px;
  background: white;
  border: 2px solid ${props => props.active ? '#ff6320' : '#e9ecef'};
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  &:hover {
    border-color: #ff6320;
  }
`;

const PreviewContainer = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OptionLabel = styled.span`
  font-size: 12px;
  color: #666;
  text-align: center;
`;
